/*
 * Copyright 2026 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import i18n.StringKey
import i18n.tr
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.GlobalScope
import kotlinx.coroutines.await
import kotlinx.coroutines.launch
import kotlinx.html.*
import kotlinx.html.dom.create
import kotlinx.html.js.*
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import org.w3c.dom.*
import org.w3c.fetch.RequestInit

@Serializable
private data class ParsedTransactionWeb(
    val date: String,
    val description: String,
    val amount: Double,
)

@Serializable
private data class ParsedStatementWeb(
    val institutionSignature: String,
    val detectedInstitution: String,
    val detectedAccountName: String,
    val detectedAccountType: String,
    val transactions: List<ParsedTransactionWeb>,
    val totalCount: Int,
    val dateFormat: String,
    val confidence: Double,
    val templateId: Long? = null,
    val parseWarnings: List<String> = emptyList(),
    val openingBalance: Double? = null,
    val closingBalance: Double? = null,
    val statementPeriodStart: String? = null,
    val statementPeriodEnd: String? = null,
)

@Serializable
private data class CreateManualAccountRequestWeb(
    val institutionName: String,
    val accountName: String,
    val accountType: String = "checking",
    val institutionSignature: String,
    val institutionAlias: String? = null,
    val transactions: List<ParsedTransactionWeb>,
    val templateId: Long? = null,
    val closingBalance: Double? = null,
)

@Serializable
private data class ManualAccountResponseWeb(
    val accountId: String,
    val transactionCount: Int,
    val message: String,
)

/** Server response for /api/accounts/{accountId}/import-transactions (append mode). */
@Serializable
private data class ImportPreviewResponseWeb(
    val parsedStatement: ParsedStatementWeb,
    val signatureMatch: Boolean = true,
    val newCount: Int = 0,
    val duplicateCount: Int = 0,
    val existingInstitution: String? = null,
)

@Serializable
private data class ConfirmImportRequestWeb(
    val transactions: List<ParsedTransactionWeb>,
    val forceImport: Boolean = false,
)

@Serializable
private data class ConfirmImportResponseWeb(
    val accountId: String? = null,
    val importedCount: Int = 0,
    val message: String? = null,
)

/**
 * Single statement-import flow used by both:
 *  - "create new manual account from a statement"  (appendToAccountId == null)
 *  - "append more transactions to an existing manual account" (appendToAccountId != null)
 *
 * Same parser, same UI shell, same Cancel/timeout safety net. Only the upload
 * endpoint and the confirm endpoint differ between modes.
 */
class StatementImportComponent(
    private val apiService: ApiService,
    private val appendToAccountId: String? = null,
    private val onComplete: (accountId: String) -> Unit = {},
    private val onBack: () -> Unit = {},
) {
    private val isAppendMode: Boolean get() = appendToAccountId != null
    private val baseUrl = if (window.location.hostname == "localhost" || window.location.hostname == "127.0.0.1") {
        "http://localhost:3000"
    } else {
        ""
    }
    private val json = Json { ignoreUnknownKeys = true; encodeDefaults = true }

    // State
    private var step: Int = 1 // 1=upload, 2=confirm, 3=success
    private var isUploading = false
    private var errorMessage: String? = null
    // Lives only while a statement upload is in flight. Holds the AbortController
    // so the Cancel button (and the 90s safety-net timeout) can abort the fetch
    // and unstick the UI when an upload hangs (huge or unparseable PDFs).
    private var currentAbortController: dynamic = null

    // Append-mode extras returned by /import-transactions (deduplication summary +
    // institution-signature drift warning). Unused in create-account mode.
    private var appendNewCount: Int = 0
    private var appendDuplicateCount: Int = 0
    private var appendSignatureMatch: Boolean = true
    private var appendExistingInstitution: String? = null

    // Parsed data (step 2)
    private var parsedStatement: ParsedStatementWeb? = null
    private var editInstitution: String = ""
    private var editAccountName: String = ""
    private var editAccountType: String = "checking"
    private var originalAccountType: String = "checking"  // Track what the server detected

    // Result (step 3)
    private var createdAccountId: String = ""
    private var importedCount: Int = 0

    fun render(): HTMLElement {
        return document.create.div {
            // Stable id so updateUI() can find and replace this exact root
            // on every state transition. Without it the old DOM tree
            // sometimes hangs around alongside the new one — leaving the
            // user looking at the parsed preview AND the upload spinner
            // AND another empty import form, all stacked.
            id = "statement-import-root"
            style = """
                display: flex;
                flex-direction: column;
                min-height: 100vh;
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
                background: linear-gradient(180deg, #0a0a0a 0%, #141418 100%);
                padding: 20px;
            """

            // Navigation Header
            div {
                style = """
                    display: flex;
                    justify-content: flex-start;
                    margin-bottom: 40px;
                """

                button {
                    style = """
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        background: none;
                        border: none;
                        color: #00d9ff;
                        font-size: 16px;
                        cursor: pointer;
                        padding: 8px 12px;
                        border-radius: 6px;
                        transition: background-color 0.2s;
                    """
                    onClickFunction = { onBack() }
                    +"\u2190 Back"
                }
            }

            // Main Content
            div {
                id = "statement-import-content"
                style = """
                    display: flex;
                    justify-content: center;
                    flex: 1;
                    align-items: center;
                """

                when (step) {
                    1 -> renderStep1Upload()
                    2 -> renderStep2Confirm()
                    3 -> renderStep3Success()
                }
            }

            // Spinner animation
            style {
                unsafe {
                    +"""
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                    """
                }
            }
        }
    }

    private fun FlowContent.renderStep1Upload() {
        div {
            style = """
                background: rgba(20, 20, 24, 0.95);
                border-radius: 16px;
                box-shadow: 0 8px 32px rgba(0, 217, 255, 0.1), 0 0 0 1px rgba(255, 255, 255, 0.05);
                padding: 48px;
                width: 100%;
                max-width: 520px;
                text-align: center;
                backdrop-filter: blur(10px);
            """

            if (isUploading) {
                // Spinner state
                div {
                    style = "display: flex; flex-direction: column; align-items: center; gap: 24px;"

                    div {
                        style = """
                            width: 64px;
                            height: 64px;
                            border: 4px solid #333;
                            border-top: 4px solid #00d9ff;
                            border-radius: 50%;
                            animation: spin 1s linear infinite;
                        """
                    }

                    h2 {
                        style = "font-size: 20px; font-weight: bold; color: #ffffff; margin: 0;"
                        +StringKey.IMPORT_ANALYZING.tr()
                    }

                    // Escape hatch: lets the user back out of a stuck/slow upload
                    // (huge PDF, weird format, network hiccup) instead of waiting
                    // for the 90s safety-net timeout.
                    button {
                        id = "statement-cancel-btn"
                        style = """
                            padding: 10px 24px;
                            border-radius: 8px;
                            border: 1px solid #555;
                            background: transparent;
                            color: #ccc;
                            font-size: 14px;
                            cursor: pointer;
                            transition: all 0.2s;
                        """
                        attributes["onmouseover"] = "this.style.borderColor='#888';this.style.color='#fff'"
                        attributes["onmouseout"] = "this.style.borderColor='#555';this.style.color='#ccc'"
                        +StringKey.ACTION_CANCEL.tr()
                    }
                }
            } else {
                // Title
                h1 {
                    style = """
                        font-size: 28px;
                        font-weight: bold;
                        color: #ffffff;
                        margin: 0 0 16px 0;
                        line-height: 1.2;
                    """
                    +StringKey.IMPORT_STATEMENT.tr()
                }

                // Description
                p {
                    style = """
                        font-size: 15px;
                        color: #999;
                        line-height: 1.6;
                        margin: 0 0 32px 0;
                    """
                    +StringKey.IMPORT_RECOMMEND_DIRECT.tr()
                }

                // Error message
                errorMessage?.let { msg ->
                    div {
                        style = """
                            background: rgba(255, 59, 48, 0.1);
                            border: 1px solid rgba(255, 59, 48, 0.3);
                            border-radius: 8px;
                            padding: 12px 16px;
                            margin-bottom: 24px;
                        """
                        p {
                            style = "color: #ff3b30; font-size: 14px; margin: 0;"
                            +msg
                        }
                    }
                }

                // Drag and drop zone
                div {
                    id = "dropzone"
                    style = """
                        border: 2px dashed #444;
                        border-radius: 16px;
                        padding: 48px 24px;
                        text-align: center;
                        cursor: pointer;
                        transition: all 0.2s;
                        margin-bottom: 16px;
                    """
                    onDragOverFunction = { e ->
                        e.preventDefault()
                        (e.currentTarget as? HTMLElement)?.style?.borderColor = "#00d9ff"
                    }
                    onDragLeaveFunction = { e ->
                        (e.currentTarget as? HTMLElement)?.style?.borderColor = "#333"
                    }
                    onDropFunction = { e ->
                        e.preventDefault()
                        (e.currentTarget as? HTMLElement)?.style?.borderColor = "#333"
                        val files = e.asDynamic().dataTransfer.files
                        if (files.length > 0) handleFile(files[0])
                    }
                    onClickFunction = {
                        (document.getElementById("fileInput") as? HTMLInputElement)?.click()
                    }

                    p {
                        style = "color: #aaa; font-size: 17px; margin-bottom: 6px; font-weight: 500;"
                        +StringKey.IMPORT_DROP_FILE.tr()
                    }
                    p {
                        style = "color: #555; font-size: 13px; margin-bottom: 6px;"
                        +"or"
                    }
                    p {
                        style = "color: #00d9ff; font-size: 15px; font-weight: 600; cursor: pointer; text-decoration: underline; text-underline-offset: 3px;"
                        +StringKey.IMPORT_SELECT_FILE.tr()
                    }
                    p {
                        style = "color: #555; font-size: 12px; margin-top: 12px;"
                        +StringKey.IMPORT_SUPPORTED_FORMATS.tr()
                    }
                    p {
                        style = "color: #444; font-size: 11px; margin-top: 16px;"
                        +"🔒 "
                        span {
                            +StringKey.IMPORT_PRIVACY_NOTE.tr().substringBefore(".")
                            +"."
                        }
                        +" "
                        a {
                            href = "#"
                            style = "color: #00d9ff; text-decoration: none;"
                            onClickFunction = { e ->
                                e.preventDefault()
                                kotlinx.browser.window.open("https://wiz3r.com/#privacy", "_blank")
                            }
                            +"Privacy Policy"
                        }
                    }
                }

                // Hidden file input
                input {
                    id = "fileInput"
                    type = InputType.file
                    attributes["accept"] = ".csv,.txt,.pdf"
                    style = "display: none;"
                    onChangeFunction = { e ->
                        val files = e.target.asDynamic().files
                        if (files != null && files.length > 0) handleFile(files[0])
                    }
                }
            }
        }
    }

    private fun FlowContent.renderStep2Confirm() {
        val statement = parsedStatement ?: return
        div {
            style = """
                background: rgba(20, 20, 24, 0.95);
                border-radius: 16px;
                box-shadow: 0 8px 32px rgba(0, 217, 255, 0.1), 0 0 0 1px rgba(255, 255, 255, 0.05);
                padding: 48px;
                width: 100%;
                max-width: 640px;
                backdrop-filter: blur(10px);
            """

            h1 {
                style = "font-size: 24px; font-weight: bold; color: #ffffff; margin: 0 0 8px 0;"
                +StringKey.IMPORT_DETECTED.tr()
            }

            // Append-mode summary: signature warning + dedup counts. The
            // institution/account-name fields aren't editable here because the
            // account already exists with its own settings.
            if (isAppendMode) {
                // Two warning shapes when signatureMatch is false:
                //  1. Confident mismatch — both bank names known and clearly
                //     different. Shows "Statement is from X but account is Y".
                //  2. Soft mismatch — parser couldn't auto-detect the bank, but
                //     the account has a known institution. Shows a generic
                //     "couldn't identify this statement, please confirm".
                val detected = statement.detectedInstitution
                val existing = appendExistingInstitution
                val parserDetectedABank =
                    detected.isNotBlank() && !detected.equals("Unknown Institution", ignoreCase = true)
                if (!appendSignatureMatch && !existing.isNullOrBlank()) {
                    div {
                        style = """
                            background: rgba(255, 152, 0, 0.12);
                            border: 1px solid rgba(255, 152, 0, 0.4);
                            border-radius: 8px;
                            padding: 12px 16px;
                            margin: 16px 0;
                            text-align: left;
                            color: #ffb74d;
                            font-size: 13px;
                            line-height: 1.5;
                        """
                        if (parserDetectedABank) {
                            +"⚠️ ${StringKey.IMPORT_MISMATCH_WARNING.tr(detected, existing)}"
                        } else {
                            +"⚠️ ${StringKey.IMPORT_MISMATCH_UNKNOWN.tr(existing)}"
                        }
                    }
                }
                div {
                    style = """
                        background: #1a1a1e;
                        border: 1px solid #333;
                        border-radius: 8px;
                        padding: 12px 16px;
                        margin: 16px 0 24px;
                        text-align: left;
                        color: #ccc;
                        font-size: 14px;
                    """
                    +StringKey.IMPORT_TOTAL_COUNT.tr(appendNewCount.toString())
                    if (appendDuplicateCount > 0) {
                        br {}
                        span {
                            style = "color: #888; font-size: 12px;"
                            +StringKey.IMPORT_DUPLICATES_SKIPPED.tr(appendDuplicateCount.toString())
                        }
                    }
                }
            }

            // Editable fields (create-account mode only — append mode keeps the
            // existing account's institution/name/type as-is).
            if (!isAppendMode) {
            div {
                style = "margin: 24px 0; text-align: left;"

                // Institution name
                label {
                    style = "display: block; color: #888; font-size: 13px; margin-bottom: 6px; font-weight: 500;"
                    +StringKey.IMPORT_INSTITUTION_NAME.tr()
                }
                input {
                    id = "edit-institution"
                    type = InputType.text
                    value = editInstitution
                    style = """
                        width: 100%;
                        padding: 12px 16px;
                        background: #1a1a1e;
                        border: 1px solid #333;
                        border-radius: 8px;
                        color: #fff;
                        font-size: 15px;
                        margin-bottom: 16px;
                        box-sizing: border-box;
                        outline: none;
                    """
                    onChangeFunction = { e ->
                        editInstitution = (e.target as? HTMLInputElement)?.value ?: editInstitution
                    }
                }

                // Account name
                label {
                    style = "display: block; color: #888; font-size: 13px; margin-bottom: 6px; font-weight: 500;"
                    +StringKey.IMPORT_ACCOUNT_NAME.tr()
                }
                input {
                    id = "edit-account-name"
                    type = InputType.text
                    value = editAccountName
                    style = """
                        width: 100%;
                        padding: 12px 16px;
                        background: #1a1a1e;
                        border: 1px solid #333;
                        border-radius: 8px;
                        color: #fff;
                        font-size: 15px;
                        margin-bottom: 16px;
                        box-sizing: border-box;
                        outline: none;
                    """
                    onChangeFunction = { e ->
                        editAccountName = (e.target as? HTMLInputElement)?.value ?: editAccountName
                    }
                }

                // Account type selector
                label {
                    style = "display: block; color: #888; font-size: 13px; margin-bottom: 6px; font-weight: 500;"
                    +StringKey.IMPORT_ACCOUNT_TYPE.tr()
                }
                select {
                    id = "edit-account-type"
                    style = """
                        width: 100%;
                        padding: 12px 16px;
                        background: #1a1a1e;
                        border: 1px solid #333;
                        border-radius: 8px;
                        color: #fff;
                        font-size: 15px;
                        margin-bottom: 24px;
                        box-sizing: border-box;
                        outline: none;
                        appearance: none;
                        -webkit-appearance: none;
                    """
                    onChangeFunction = { e ->
                        val newType = (e.target as? HTMLSelectElement)?.value ?: editAccountType
                        val wasCredit = editAccountType == "credit"
                        val isNowCredit = newType == "credit"
                        editAccountType = newType
                        // If crossing credit↔non-credit boundary, flip all transaction signs
                        if (wasCredit != isNowCredit && parsedStatement != null) {
                            parsedStatement = parsedStatement!!.copy(
                                transactions = parsedStatement!!.transactions.map { it.copy(amount = -it.amount) }
                            )
                            // Update the preview table in-place
                            val previewCells = document.querySelectorAll(".txn-amount-cell")
                            val txns = parsedStatement!!.transactions
                            for (i in 0 until previewCells.length) {
                                val cell = previewCells.item(i) as? HTMLElement ?: continue
                                if (i < txns.size) {
                                    val displayAmount = -txns[i].amount
                                    val color = if (displayAmount < 0) "#ff3b30" else "#4cd964"
                                    cell.style.color = color
                                    cell.textContent = formatAmount(displayAmount)
                                }
                            }
                        }
                    }
                    option {
                        value = "checking"
                        selected = editAccountType == "checking"
                        +StringKey.IMPORT_ACCOUNT_TYPE_CHECKING.tr()
                    }
                    option {
                        value = "savings"
                        selected = editAccountType == "savings"
                        +StringKey.IMPORT_ACCOUNT_TYPE_SAVINGS.tr()
                    }
                    option {
                        value = "credit"
                        selected = editAccountType == "credit"
                        +StringKey.IMPORT_ACCOUNT_TYPE_CREDIT.tr()
                    }
                }
            }
            } // end if (!isAppendMode)

            // Transaction preview
            div {
                style = """
                    background: #1a1a1e;
                    border: 1px solid #333;
                    border-radius: 12px;
                    padding: 20px;
                    margin-bottom: 24px;
                """

                h3 {
                    style = "font-size: 16px; font-weight: 600; color: #ccc; margin: 0 0 16px 0;"
                    +StringKey.IMPORT_TRANSACTIONS_PREVIEW.tr()
                }

                // Preview table
                div {
                    style = "overflow-x: auto;"

                    table {
                        style = """
                            width: 100%;
                            border-collapse: collapse;
                            font-size: 13px;
                        """

                        thead {
                            tr {
                                th { style = "text-align: left; padding: 8px 12px; color: #666; border-bottom: 1px solid #333; font-weight: 500;"; +StringKey.IMPORT_COLUMN_DATE.tr() }
                                th { style = "text-align: left; padding: 8px 12px; color: #666; border-bottom: 1px solid #333; font-weight: 500;"; +StringKey.IMPORT_COLUMN_DESCRIPTION.tr() }
                                th { style = "text-align: right; padding: 8px 12px; color: #666; border-bottom: 1px solid #333; font-weight: 500;"; +StringKey.IMPORT_COLUMN_AMOUNT.tr() }
                            }
                        }

                        tbody {
                            val preview = statement.transactions.take(10)
                            for (txn in preview) {
                                tr {
                                    td { style = "padding: 8px 12px; color: #ccc; border-bottom: 1px solid #222;"; +txn.date }
                                    td {
                                        style = "padding: 8px 12px; color: #fff; border-bottom: 1px solid #222; max-width: 250px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;"
                                        +txn.description
                                    }
                                    td {
                                        // Parser returns Plaid convention (positive=expense, negative=income)
                                        // Display in natural convention: negative=expense (red), positive=income (green)
                                        val displayAmount = -txn.amount
                                        val color = if (displayAmount < 0) "#ff3b30" else "#4cd964"
                                        classes = setOf("txn-amount-cell")
                                        style = "padding: 8px 12px; color: $color; border-bottom: 1px solid #222; text-align: right; font-variant-numeric: tabular-nums;"
                                        +formatAmount(displayAmount)
                                    }
                                }
                            }
                        }
                    }
                }

                // Total count
                p {
                    style = "color: #888; font-size: 13px; margin: 12px 0 0 0;"
                    +StringKey.IMPORT_TOTAL_COUNT.tr().replace("%s", statement.totalCount.toString())
                }
            }

            // Action buttons
            div {
                style = "display: flex; flex-direction: column; gap: 12px; align-items: center;"

                // Looks good button
                button {
                    id = "confirm-import-btn"
                    style = """
                        width: 100%;
                        height: 52px;
                        background: linear-gradient(135deg, #00d9ff 0%, #00b8d9 100%);
                        color: #0a0a0a;
                        border: none;
                        border-radius: 26px;
                        font-size: 17px;
                        font-weight: 600;
                        cursor: pointer;
                        transition: all 0.2s ease;
                        box-shadow: 0 4px 12px rgba(0, 217, 255, 0.3);
                    """
                    onClickFunction = { handleConfirm() }
                    +StringKey.IMPORT_LOOKS_GOOD.tr()
                }

                // Doesn't look right button
                button {
                    style = """
                        background: transparent;
                        border: 1px solid #333;
                        color: #888;
                        padding: 12px 24px;
                        border-radius: 8px;
                        font-size: 14px;
                        cursor: pointer;
                        width: 100%;
                        transition: all 0.2s;
                    """
                    onMouseOverFunction = { (it.currentTarget as? HTMLElement)?.style?.borderColor = "#666" }
                    onMouseOutFunction = { (it.currentTarget as? HTMLElement)?.style?.borderColor = "#333" }
                    onClickFunction = { handleRetry() }
                    +StringKey.IMPORT_DOESNT_LOOK_RIGHT.tr()
                }
            }
        }
    }

    private fun FlowContent.renderStep3Success() {
        div {
            style = """
                background: rgba(20, 20, 24, 0.95);
                border-radius: 16px;
                box-shadow: 0 8px 32px rgba(0, 217, 255, 0.1), 0 0 0 1px rgba(255, 255, 255, 0.05);
                padding: 48px;
                width: 100%;
                max-width: 480px;
                text-align: center;
                backdrop-filter: blur(10px);
            """

            // Success icon
            div {
                style = "font-size: 64px; margin-bottom: 24px;"
                +"\u2705"
            }

            h1 {
                style = "font-size: 24px; font-weight: bold; color: #ffffff; margin: 0 0 16px 0;"
                +StringKey.IMPORT_SUCCESS.tr()
                    .replace("%s", importedCount.toString(), false)
                    .replaceFirst("%s", editAccountName)
            }

            // View transactions button
            button {
                style = """
                    width: 100%;
                    max-width: 320px;
                    height: 52px;
                    background: linear-gradient(135deg, #00d9ff 0%, #00b8d9 100%);
                    color: #0a0a0a;
                    border: none;
                    border-radius: 26px;
                    font-size: 17px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 12px rgba(0, 217, 255, 0.3);
                    margin-top: 24px;
                """
                onClickFunction = { onComplete(createdAccountId) }
                +StringKey.NAV_TRANSACTIONS.tr()
            }
        }
    }

    private fun handleFile(file: dynamic) {
        console.log("📄 handleFile called: ${file.name}, size=${file.size}")
        isUploading = true
        errorMessage = null
        // Fresh AbortController for this upload — cancel button + safety-net
        // timeout both call .abort() on it to unblock the awaited fetch.
        val controller = js("new AbortController()")
        currentAbortController = controller
        updateUI()

        val token = ApiService.getSharedAuthToken()
        if (token == null) {
            isUploading = false
            errorMessage = "Authentication required"
            updateUI()
            return
        }

        // Wire the cancel button + a 90s safety-net timeout. If the user clicks
        // cancel, or the request runs past the timeout, we abort the fetch and
        // surface a friendly message instead of leaving the spinner forever.
        kotlinx.browser.window.setTimeout({
            (document.getElementById("statement-cancel-btn") as? HTMLButtonElement)?.addEventListener("click", { _ ->
                console.log("📄 User cancelled statement import")
                try { controller.abort() } catch (_: Throwable) {}
            })
        }, 50)
        val safetyTimeoutId = kotlinx.browser.window.setTimeout({
            if (isUploading && currentAbortController == controller) {
                console.log("📄 Statement import safety-net timeout (90s) — aborting")
                try { controller.abort() } catch (_: Throwable) {}
            }
        }, 90_000)

        val authToken = token
        GlobalScope.launch {
            try {
                console.log("📄 Uploading statement file: ${file.name}")

                // Mode-dependent endpoint. /import-statement parses for new-account
                // creation; /{accountId}/import-transactions parses + dedupes against
                // an existing account's transactions.
                val fetchUrl = if (isAppendMode) {
                    "$baseUrl/api/accounts/$appendToAccountId/import-transactions"
                } else {
                    "$baseUrl/api/accounts/import-statement"
                }
                console.log("📄 Fetch URL: $fetchUrl")

                // Use eval to build the fetch call entirely in JS land
                val responsePromise = js("""
                    (function(url, authHeader, fileObj, signal) {
                        var fd = new FormData();
                        fd.append('file', fileObj);
                        return fetch(url, {
                            method: 'POST',
                            headers: { 'Authorization': authHeader },
                            body: fd,
                            signal: signal
                        });
                    })
                """)(fetchUrl, "Bearer $authToken", file, controller.signal)

                val response = responsePromise.unsafeCast<kotlin.js.Promise<org.w3c.fetch.Response>>().await()
                console.log("📄 Response status: ${response.status}")

                if (!response.ok) {
                    // Don't dump raw response bodies into the UI — nginx 413
                    // pages are big chunks of HTML, server JSON errors look
                    // like {"error":"..."}. Translate to a friendly message.
                    val rawBody = try { response.text().await() } catch (_: Exception) { "" }
                    val friendly = when (response.status.toInt()) {
                        413 -> "File is too large to upload. Please try a statement under ~10 MB, or split it into smaller date ranges."
                        400 -> {
                            // Best-effort parse of a JSON {"error":"..."} response.
                            val jsonError = runCatching {
                                val el = kotlinx.serialization.json.Json.parseToJsonElement(rawBody)
                                el.asDynamic().error?.unsafeCast<String?>() ?: el.asDynamic().message?.unsafeCast<String?>()
                            }.getOrNull() as? String
                            jsonError ?: "We couldn't read this statement. Make sure it's a valid CSV, TXT, or PDF file from your bank."
                        }
                        401, 403 -> "Your session expired while uploading. Please sign in again and retry."
                        415 -> "That file type isn't supported. Please upload a CSV, TXT, or PDF statement."
                        in 500..599 -> "Our server hiccupped while parsing the statement. Please try again in a moment."
                        else -> "Upload failed (HTTP ${response.status}). Please try a different file."
                    }
                    throw Exception(friendly)
                }

                val responseText = response.text().await()
                // Append mode returns ImportPreviewResponse (wraps parsedStatement +
                // signatureMatch + newCount + duplicateCount). Create mode returns
                // a flat ParsedStatement.
                val parsed: ParsedStatementWeb
                if (isAppendMode) {
                    val preview = json.decodeFromString<ImportPreviewResponseWeb>(responseText)
                    parsed = preview.parsedStatement
                    appendNewCount = preview.newCount
                    appendDuplicateCount = preview.duplicateCount
                    appendSignatureMatch = preview.signatureMatch
                    appendExistingInstitution = preview.existingInstitution
                } else {
                    parsed = json.decodeFromString<ParsedStatementWeb>(responseText)
                }

                parsedStatement = parsed
                editInstitution = parsed.detectedInstitution
                editAccountName = parsed.detectedAccountName
                // Normalize: server may return "credit_card", UI uses "credit"
                val normalizedType = if (parsed.detectedAccountType.contains("credit")) "credit" else parsed.detectedAccountType.ifBlank { "checking" }
                editAccountType = normalizedType
                originalAccountType = normalizedType
                isUploading = false
                currentAbortController = null
                kotlinx.browser.window.clearTimeout(safetyTimeoutId)
                step = 2
                updateUI()
            } catch (e: Throwable) {
                console.error("Statement import error:", e)
                isUploading = false
                kotlinx.browser.window.clearTimeout(safetyTimeoutId)
                // Distinguish a user-initiated cancel / safety-net timeout from
                // a real failure. The AbortController.signal.aborted flag is the
                // most reliable detector — it's true iff we (or the safety-net)
                // called abort(), and works regardless of how the underlying
                // fetch surfaced the error in JS-land.
                val wasAborted = try {
                    controller.signal.aborted as? Boolean == true
                } catch (_: Throwable) { false }
                errorMessage = when {
                    wasAborted -> StringKey.IMPORT_CANCELLED_MESSAGE.tr()
                    else -> e.message ?: StringKey.IMPORT_FAILED_GENERIC.tr("")
                }
                currentAbortController = null
                updateUI()
            }
        }
    }

    private fun handleConfirm() {
        val statement = parsedStatement ?: return

        // Create-account mode reads back the editable institution/name/type; append
        // mode has no such inputs (the account already exists).
        if (!isAppendMode) {
            editInstitution = (document.getElementById("edit-institution") as? HTMLInputElement)?.value ?: editInstitution
            editAccountName = (document.getElementById("edit-account-name") as? HTMLInputElement)?.value ?: editAccountName
            editAccountType = (document.getElementById("edit-account-type") as? HTMLSelectElement)?.value ?: editAccountType
        }

        val token = ApiService.getSharedAuthToken() ?: return

        // Disable button so a slow network doesn't get double-submitted
        (document.getElementById("confirm-import-btn") as? HTMLButtonElement)?.let {
            it.disabled = true
            it.style.opacity = "0.5"
            it.style.cursor = "not-allowed"
        }

        GlobalScope.launch {
            try {
                val headers = js("{}")
                headers["Authorization"] = "Bearer $token"
                headers["Content-Type"] = "application/json"

                val options = js("{}")
                options["method"] = "POST"
                options["headers"] = headers

                val (url, body) = if (isAppendMode) {
                    val req = ConfirmImportRequestWeb(
                        transactions = statement.transactions,
                        // Bypass signature-mismatch warning if the user confirmed
                        // anyway in step 2 (we didn't bounce them back).
                        forceImport = !appendSignatureMatch,
                    )
                    "$baseUrl/api/accounts/$appendToAccountId/confirm-import" to
                        json.encodeToString(ConfirmImportRequestWeb.serializer(), req)
                } else {
                    val req = CreateManualAccountRequestWeb(
                        institutionName = editInstitution,
                        accountName = editAccountName,
                        accountType = editAccountType,
                        institutionSignature = statement.institutionSignature,
                        transactions = statement.transactions,
                        templateId = statement.templateId,
                        closingBalance = statement.closingBalance,
                    )
                    "$baseUrl/api/accounts/create-manual" to
                        json.encodeToString(CreateManualAccountRequestWeb.serializer(), req)
                }

                options["body"] = body
                val response = window.fetch(url, options).await()
                if (!response.ok) {
                    val errorText = response.text().await()
                    throw Exception("Import failed: $errorText")
                }

                val responseText = response.text().await()
                if (isAppendMode) {
                    val result = json.decodeFromString<ConfirmImportResponseWeb>(responseText)
                    createdAccountId = appendToAccountId ?: ""
                    importedCount = result.importedCount.takeIf { it > 0 } ?: appendNewCount
                } else {
                    val result = json.decodeFromString<ManualAccountResponseWeb>(responseText)
                    createdAccountId = result.accountId
                    importedCount = result.transactionCount
                }
                step = 3
                updateUI()
            } catch (e: Exception) {
                console.error("Statement import confirm error:", e)
                showAppAlert(StringKey.IMPORT_FAILED_GENERIC.tr(e.message ?: ""))
                (document.getElementById("confirm-import-btn") as? HTMLButtonElement)?.let {
                    it.disabled = false
                    it.style.opacity = "1"
                    it.style.cursor = "pointer"
                }
            }
        }
    }

    private fun handleRetry() {
        step = 1
        parsedStatement = null
        errorMessage = null
        isUploading = false
        updateUI()
    }

    private fun updateUI() {
        // Replace this exact root element by id. Avoids the prior
        // approach of "find any element with min-height: 100vh in inline
        // style and pray it's ours" — which falsely matched siblings
        // and left stale DOM around between state transitions.
        val existing = document.getElementById("statement-import-root")
        if (existing != null) {
            existing.parentNode?.replaceChild(render(), existing)
        }
    }

    private fun formatAmount(amount: Double): String {
        val absAmount = kotlin.math.abs(amount)
        val formatted = absAmount.asDynamic().toFixed(2) as String
        return if (amount < 0) "-\$$formatted" else "\$$formatted"
    }
}
